import { useMemo, useEffect, useState } from "react";
import Modal from '../Modal';
import styles from './registerModal.module.scss';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import countries from '../../constants/countries.json';
import { formatRut, validateRut } from "../../helpers/app";
import Validation from "../../pages/Register/Validation";
import TextFieldShared from "../TextField/TextField";
import SelectShared from "../Select/Select";
import CheckboxShared from "../Checkbox/Checkbox";
import ButtonShared from "../Button/Button";
import PhoneInput from "../PhoneInput/PhoneInput";
import { AnalyticsService } from "../../helpers/analytics";
import TermsModalContainer from "../../containers/TermsModal/TermsModalContainer";
import { images } from "../../constants/images";

interface RegisterModalProps {
    open: boolean;
    onClose: () => void;
    onSwitchToLogin: () => void;
    authFetching: string;
    loginRequest: Function;
    registerRequest: Function;
    register: any;
    cleanErrorRegister: Function;
    externalUser: any;
    externalLogin: any;
    cleanExternalLogin: Function;
    getPermissionType: Function;
    permissionType: any;
    isGoogleLogin?: boolean;
}

const RegisterModal = ({
    open,
    onClose,
    onSwitchToLogin,
    authFetching,
    loginRequest,
    registerRequest,
    register,
    cleanErrorRegister,
    externalUser,
    externalLogin,
    cleanExternalLogin,
    getPermissionType,
    permissionType,
    isGoogleLogin = false
}: RegisterModalProps) => {
    const [validationPage, setValidationPage] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [name, setName] = useState<any>({ value: "", error: "" });
    const [lastName, setLastName] = useState<any>({ value: "", error: "" });
    const [email, setEmail] = useState<any>({ value: "", error: "" });
    const [rut, setRut] = useState<any>({ value: "", error: "" });
    const [password, setPassword] = useState<any>({
        value: "",
        error: "",
        validation: {
            specialCaracter: false,
            mayus: false,
            correctLength: false,
            number: false,
            minus: false
        }
    });
    const [countrySelected, setCountrySelected] = useState<any>({ value: "", error: "" });
    const [phoneValue, setPhoneValue] = useState<any>({ code: "+56", number: "", error: "" });
    const [phoneInputValue, setPhoneInputValue] = useState<string>("");
    const [termsCheck, setTermsCheck] = useState<boolean>(false);
    const [marketingCheck, setMarketingCheck] = useState<boolean>(false);
    const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);

    const handleCheck = () => {
        setTermsCheck(!termsCheck);
    }

    const handleOpenTermsModal = () => {
        setTermsModalOpen(true);
    }

    const handleCloseTermsModal = () => {
        setTermsModalOpen(false);
    }

    const handleCheckMarketing = () => {
        setMarketingCheck(!marketingCheck);
    }

    const handleChangeCountry = (name: any) => {
        setCountrySelected({ countrySelected, value: name });
    }

    const handleSwitchForm = () => {
        onSwitchToLogin();
        handleClose();
    }

    const handleChangePhone = (value: any, type: string, inputValue?: string) => {
        if (type === "code") {
            setPhoneValue({ ...phoneValue, code: value });
        } else {
            setPhoneValue({ ...phoneValue, number: value });
            if (inputValue !== undefined) {
                setPhoneInputValue(inputValue);
            }
        }
    }

    const handleChangeRut = (value: any) => {
        const formatValue = formatRut(value);
        if (!validateRut(formatValue)) {
            return setRut({ value: formatValue, error: "Ingresa un rut válido" });
        }
        setRut({ value: formatValue, error: "" });
    }

    const handleClose = () => {
        onClose();
        setValidationPage(false);
        setName({ value: "", error: "" });
        setLastName({ value: "", error: "" });
        setEmail({ value: "", error: "" });
        setRut({ value: "", error: "" });
        setPassword({
            value: "", error: "", validation: {
                specialCaracter: false,
                mayus: false,
                correctLength: false,
                number: false,
                minus: false
            }
        });
        setCountrySelected({ value: "", error: "" });
        setPhoneValue({ code: "+56", number: "", error: "" });
        setTermsCheck(false);
        setMarketingCheck(false);
    };

    const handleSubmit = () => {
        if (!name?.value) { return setName({ ...name, error: "Debes ingresar un nombre" }) } else setName({ ...name, error: "" });
        if (!lastName?.value) { return setLastName({ ...lastName, error: "Debes ingresar un apellido" }) } else setLastName({ ...lastName, error: "" });;
        if (!email?.value) { return setEmail({ ...email, error: "Debes ingresar un email" }) } else setEmail({ ...email, error: "" });
        if (!rut?.value) { return setRut({ ...rut, error: "Debes ingresar un rut" }) } else setRut({ ...rut, error: "" });
        if (!phoneValue?.number) { return setPhoneValue({ ...phoneValue, error: "Debes ingresar un telefono" }) } else setPhoneValue({ ...phoneValue, error: "" });

        setValidationPage(true);
        if (!validateRut(rut?.value)) {
            setValidationPage(false);
            return setRut({ ...rut, error: "RUT incorrecto" });
        }

        const phone = {
            code: phoneValue?.code,
            number: phoneValue?.number,
        }
        AnalyticsService.trackSignUp('email');

        registerRequest({
            name: name?.value,
            last_name: lastName?.value,
            country: 'Chile',
            password: password?.value,
            legal_id: formatRut(rut?.value),
            email: email?.value,
            phone: phone,
            company: process.env.REACT_APP_COMPANY,
            profile: process.env.REACT_APP_PROFILE,
            permissions: [{
                type: permissionType?.data?.permissiongrants_type?.[0]?._id,
                granted: marketingCheck
            }]
        });
    }

    const isButtonDisabled: boolean = useMemo(() => {
        if (isGoogleLogin) {
            return !termsCheck || !name?.value || !lastName?.value || !email?.value || !rut?.value || !phoneInputValue || phoneInputValue?.length === 0;
        }
        return !termsCheck || !name?.value || !lastName?.value || !email?.value || rut?.error || !phoneInputValue || phoneInputValue?.length === 0 || !(password?.validation?.specialCaracter && password?.validation?.mayus && password?.validation?.correctLength && password?.validation?.number && password?.validation?.minus)
    }, [termsCheck, marketingCheck, password?.validation, isGoogleLogin, name?.value, lastName?.value, email?.value, rut?.value, phoneInputValue, phoneInputValue?.length]);

    useEffect(() => {
        if (register.fetching === "failure") {
            setValidationPage(false);
            if (register?.error === "EMAIL_ALREADY_EXISTS") {
                return setEmail({ ...email, error: "El email ya existe" });
            }
            if (register?.error === "LEGAL_ID_ALREADY_EXISTS") {
                return setRut({ ...rut, error: "El rut ya existe" });
            }
            if (register?.error === "PHONE_ALREADY_EXISTS") {
                return setPhoneValue({ ...phoneValue, error: "El telefono ya existe" });
            }
            if (register?.error === "EMAIL_INVALID_EMAIL") {
                return setEmail({ ...email, error: "Ingresa un email válido linze@ejemplo.com" });
            }
        }
        if (register.fetching === "success") {
            cleanExternalLogin();
            loginRequest({
                email: email?.value,
                password: password?.value
            });
        }
    }, [register.fetching]);

    useEffect(() => {
        if (authFetching === "success" && !externalLogin) {
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                handleClose();
            }, 1500);
        }
        if (authFetching === "failure" && !externalLogin) {
            handleClose();
            onSwitchToLogin();
            cleanErrorRegister();
        }
    }, [authFetching]);

    useEffect(() => {
        if (externalUser) {
            setName({ value: externalUser.name, error: "" });
            setLastName({ value: externalUser.last_name, error: "" });
            setEmail({ value: externalUser.email, error: "" });
        }
    }, [externalUser]);

    useEffect(() => {
        if (open) {
            if (externalUser) {
                setName({ value: externalUser.name, error: "" });
                setLastName({ value: externalUser.last_name, error: "" });
                setEmail({ value: externalUser.email, error: "" });

                if (isGoogleLogin && externalUser.email) {
                    const defaultPassword = `${externalUser.name}${externalUser.last_name}123!`;
                    setPassword({
                        value: defaultPassword,
                        error: "",
                        validation: {
                            specialCaracter: true,
                            mayus: true,
                            correctLength: true,
                            number: true,
                            minus: true
                        }
                    });
                }
            }
            getPermissionType("marketing", "register");
        }
    }, [open, isGoogleLogin]);

    return (
        <Modal variant="" open={open} onClose={handleClose} className={styles.modal}>
            <div className={styles.content}>
                {showSuccess ? (
                    <div className={styles.successContainer}>
                        <div className={styles.checkCircle}>
                            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                                <circle cx="40" cy="40" r="38" stroke="#4CAF50" strokeWidth="4" fill="none" />
                                <path d="M25 40L35 50L55 30" stroke="#4CAF50" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h2 className={styles.successTitle}>¡Todo listo!</h2>
                        <p className={styles.successMessage}>Ya tienes una cuenta en Linze</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.btnClose}>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </div>

                        {validationPage ? (
                            <Validation />
                        ) : (
                            <>


                                <div className={styles.iconContainer}>
                                    <img src={images.logoNLinze} className={styles.icon} />
                                </div>

                                <p className={styles.title}>Regístrate en Linze</p>
                                {isGoogleLogin && (
                                    <p className={styles.googleText}>Termina de crear tu cuenta en Linze para poder acceder a todas sus funciones</p>
                                )}
                                <div className={styles.registerContent}>
                                    <TextFieldShared
                                        label="Nombre"
                                        name="nameInput"
                                        value={name?.value}
                                        error={name?.error}
                                        checker="onlyLetters"
                                        onChange={(value: any) => setName({ ...name, value: value })}
                                        className={styles.half}
                                        variant="loginForm"
                                    />
                                    <TextFieldShared
                                        label="Apellido/s"
                                        name="lastNameInput"
                                        value={lastName?.value}
                                        error={lastName?.error}
                                        checker="onlyLetters"
                                        onChange={(value: any) => setLastName({ ...lastName, value: value })}
                                        className={styles.half}
                                        variant="loginForm"
                                    />
                                    <TextFieldShared
                                        label="RUT"
                                        name="rutInput"
                                        value={rut?.value}
                                        error={rut?.error}
                                        className={styles.half}
                                        onChange={handleChangeRut}
                                        variant="loginForm"
                                    />
                                    <PhoneInput
                                        label="Teléfono"
                                        numberValue={phoneValue.number}
                                        codeValue={phoneValue.code}
                                        onChange={handleChangePhone}
                                        error={phoneValue?.error}
                                        className={styles.half}
                                        variant="loginForm"
                                    />
                                    <TextFieldShared
                                        label="E-mail"
                                        name="emailInput"
                                        value={email?.value}
                                        error={email?.error}
                                        onChange={(value: any) => setEmail({ ...email, value: value })}
                                        className={styles.full}
                                        variant="loginForm"
                                    />
                                    {!isGoogleLogin && (
                                        <TextFieldShared
                                            type="password"
                                            label="Contraseña"
                                            name="passwordInput"
                                            value={password?.value}
                                            error={password?.error}
                                            onChange={(value: any) => setPassword({ ...password, value: value?.value, validation: value?.passValidate })}
                                            className={styles.full}
                                            variant="loginForm"
                                        />
                                    )}
                                    <div className={styles.termsCheck}>
                                        <CheckboxShared
                                            onPress={handleCheck}
                                            className={`${styles.termsConditions} ${styles.full}`}
                                            id="termsCheckbox"
                                            text={
                                                <p className={styles.message}>
                                                    <span>He leído los </span>
                                                    <span className={styles.bold}><a className={styles.boldText} onClick={handleOpenTermsModal}>Términos y condiciones</a></span>
                                                    <span> de Linze</span>
                                                </p>
                                            }
                                            checked={termsCheck}
                                        />
                                        <CheckboxShared
                                            onPress={handleCheckMarketing}
                                            className={`${styles.termsConditions} ${styles.full}`}
                                            id="marketingCheckbox"
                                            text={
                                                <p className={styles.message}>
                                                    <span>Acepto recibir comunicaciones relacionadas a Linze y sus servicios</span>
                                                </p>
                                            }
                                            checked={marketingCheck}
                                        />
                                    </div>
                                </div>

                                <ButtonShared
                                    title="Crear cuenta"
                                    className={styles.submit}
                                    disabled={isButtonDisabled}
                                    primaryLogin
                                    onPress={handleSubmit}
                                />
                                {!isGoogleLogin && (
                                    <div className={styles.link}>
                                        <p>¿Ya tienes cuenta?</p>
                                        <span className={styles.redirect} onClick={handleSwitchForm}>Inicia sesión</span>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            <TermsModalContainer open={termsModalOpen} onClose={handleCloseTermsModal} />
        </Modal>
    );
};

export default RegisterModal;
